/**
 * /platform/videos — SEO landing page for the Video Library.
 */
import React from 'react';
import {
  Video, PlayCircle, Filter, Star, Clock, Bookmark, ThumbsUp,
  Users, BookOpen, Bot, Sparkles, Layers, Rocket, ChefHat, Cog,
} from 'lucide-react';
import PlatformFeaturePage from './PlatformFeaturePage';

const VideoLibraryPage = () => (
  <PlatformFeaturePage
    slug="videos"
    eyebrow="Video Library"
    title="Every freeze-dry video worth"
    titleAccent="watching, in one place."
    subtitle="A curated, categorized library of the best tutorials, deep-dives and shop tours from across YouTube and beyond — no algorithm rabbit-hole required."
    seo={{
      title: 'Freeze Dry Video Library — Curated Tutorials & Deep-Dives',
      description: 'Hand-picked freeze-drying videos organized by topic: cycles, machines, business, troubleshooting. Save your favourites and pick up where you left off.',
      keywords: 'freeze dry videos, freeze drying tutorials, freeze dryer videos, freeze dry youtube collection',
    }}
    highlights={[
      { icon: Filter,   label: 'Filter by topic + machine' },
      { icon: Star,     label: 'Community-rated' },
      { icon: Bookmark, label: 'Save + resume' },
      { icon: Clock,    label: 'Fresh videos weekly' },
    ]}
    primaryCta={{ label: 'Browse the library', to: '/login?mode=signup' }}
    secondaryCta={{ label: 'See Learning courses', to: '/platform/learning' }}
    howItWorks={[
      { title: 'Browse by what you need',        body: 'Filter by topic (cycles, business, troubleshooting), machine size (small / medium / large / commercial), or level (beginner → advanced).' },
      { title: 'Watch inline, save the good ones',body: 'Videos embed right in the app so you don\'t bounce to YouTube and lose the thread. Star the winners for later.' },
      { title: 'Pick up where you left off',     body: 'Your watch history follows you across devices. Half-way through a shop tour last night? It resumes from the second.' },
    ]}
    features={[
      { icon: Layers,     title: 'Categorized by topic',   body: 'Not "freeze-dry stuff" — actual buckets like "primary drying tuning", "vacuum pump maintenance", "packaging + Mylar", "business ops".' },
      { icon: Star,       title: 'Community-rated',        body: 'Operators rate every video 1-5 stars with a short note on what it\'s good for. Cuts out the fluff channels fast.' },
      { icon: ThumbsUp,   title: 'Editor picks',           body: 'The academy team pins the 2-3 best videos in each category so newcomers can start with the gold standard.' },
      { icon: Bookmark,   title: 'Favourites + collections',body: 'Build your own playlist for training staff or your own reference — "everything I want to rewatch before scaling"' },
      { icon: PlayCircle, title: 'Embedded playback',      body: 'YouTube-hosted for reliability, but embedded in-app with keyboard shortcuts and no algorithm feed.' },
      { icon: Sparkles,   title: 'New every week',         body: 'We add hand-picked videos every week. Follow a category to get a notification when new ones land.' },
    ]}
    personas={[
      { icon: Rocket,  title: 'Absolute beginners',   body: 'Start with the Editor Picks in the "First 30 days" collection — a curated on-ramp before you buy your first tray.' },
      { icon: ChefHat, title: 'Recipe developers',    body: 'The Product Playbooks category groups videos by food — dairy, meats, eggs, produce, meals — for concrete cycle guidance.' },
      { icon: Cog,     title: 'Machine tinkerers',    body: 'Pump rebuilds, gasket swaps, seal replacements — the maintenance section is a maintenance manual in video form.' },
    ]}
    faqs={[
      { q: 'Do you host the videos or link out?',
        a: 'We embed YouTube and Vimeo videos and pay creators through their platforms. The academy team curates + categorizes, so you get one canonical library instead of 47 open tabs.' },
      { q: 'Can I submit a video?',
        a: 'Yes — every video page has a "Suggest a video" link. Community submissions with 3+ upvotes get reviewed by the editor team.' },
      { q: 'Does watch history sync across devices?',
        a: 'Yes — sign in on the phone next to your machine and the app knows exactly where you left off on the desktop.' },
      { q: 'Do you strip out spammy channels?',
        a: 'Yes. If a channel is monetising bad advice or clickbaiting, it doesn\'t make the library. Curation is the whole point.' },
    ]}
    related={[
      { slug: 'learning',    title: 'Learning & Courses', icon: BookOpen, blurb: 'When you need the full structured curriculum.' },
      { slug: 'community',   title: 'Community Forum',    icon: Users,    blurb: 'Discuss what you just watched with real operators.' },
      { slug: 'ai-assistant',title: 'AI Assistant',       icon: Bot,      blurb: 'Ask "what was the cycle temp they mentioned in that video?"' },
    ]}
    finalCtaTitle="Stop losing hours to the YouTube algorithm."
    finalCtaBody="Sign up free — the library is included on every tier."
  />
);

export default VideoLibraryPage;
